import type { AuthResponse, AuthState } from '~/types';

import { decodeJwtPayload } from './utils';

// =============================================================================
// Storage Keys (namespaced to avoid collisions)
// =============================================================================

const STORAGE_KEYS = {
  accessToken: 'yak_access_token',
  refreshToken: 'yak_refresh_token',
  userId: 'yak_user_id',
  expiresAt: 'yak_expires_at',
  slug: 'yak_slug',
} as const;

// Default TTL: 1 hour in milliseconds (fallback if JWT exp claim unavailable)
const DEFAULT_TTL_MS = 60 * 60 * 1000;

// =============================================================================
// Storage Helpers (graceful degradation if localStorage unavailable)
// =============================================================================

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

// =============================================================================
// Token Management
// =============================================================================

/**
 * Retrieves the current auth state from localStorage.
 */
export function getTokens(): AuthState {
  return {
    accessToken: safeGetItem(STORAGE_KEYS.accessToken),
    refreshToken: safeGetItem(STORAGE_KEYS.refreshToken),
    userId: safeGetItem(STORAGE_KEYS.userId),
    expiresAt: safeGetItem(STORAGE_KEYS.expiresAt)
      ? Number(safeGetItem(STORAGE_KEYS.expiresAt))
      : null,
    slug: safeGetItem(STORAGE_KEYS.slug),
  };
}

/**
 * Stores auth tokens from the API response.
 *
 * Field mapping (API → localStorage):
 * - response.token → yak_access_token (API field is `token`, not `accessToken`)
 * - response.refreshToken → yak_refresh_token
 * - response.user.id → yak_user_id (nested under `user` object)
 * - Derived from JWT → yak_expires_at (decode JWT `exp` claim, or use fixed 1h TTL)
 * - API `redirectUrl` → IGNORED (play app manages its own funnel)
 *
 * Only stores tokens if the auth response indicates success.
 */
export function setTokens(apiResponse: AuthResponse, slug?: string): void {
  // Only store tokens if the response is successful
  if (!apiResponse.success) {
    return;
  }

  safeSetItem(STORAGE_KEYS.accessToken, apiResponse.token);
  // refreshToken may not be returned if refresh tokens are disabled in FusionAuth
  if (apiResponse.refreshToken) {
    safeSetItem(STORAGE_KEYS.refreshToken, apiResponse.refreshToken);
  }
  safeSetItem(STORAGE_KEYS.userId, apiResponse.user.id);

  // Derive expiresAt from JWT exp claim, fallback to fixed TTL
  let expiresAt: number;
  const payload = decodeJwtPayload(apiResponse.token);
  if (payload && typeof payload.exp === 'number') {
    expiresAt = payload.exp * 1000; // JWT exp is in seconds, convert to ms
  } else {
    expiresAt = Date.now() + DEFAULT_TTL_MS;
  }
  safeSetItem(STORAGE_KEYS.expiresAt, expiresAt.toString());

  if (slug) {
    safeSetItem(STORAGE_KEYS.slug, slug);
  }
}

/**
 * Clears all auth tokens from localStorage.
 */
export function clearTokens(): void {
  safeRemoveItem(STORAGE_KEYS.accessToken);
  safeRemoveItem(STORAGE_KEYS.refreshToken);
  safeRemoveItem(STORAGE_KEYS.userId);
  safeRemoveItem(STORAGE_KEYS.expiresAt);
  safeRemoveItem(STORAGE_KEYS.slug);
}

/**
 * Checks if the user currently has a valid auth session.
 * Returns true if there's a non-expired access token OR a refresh token
 * that can be used to obtain a new access token.
 */
export function isAuthenticated(): boolean {
  const tokens = getTokens();
  // User is authenticated if they have a valid access token or a refresh token
  if (tokens.accessToken && !isExpired()) return true;
  if (tokens.refreshToken) return true;
  return false;
}

/**
 * Checks if the stored auth token has expired.
 * Returns true if expired or no expiry set.
 */
export function isExpired(): boolean {
  const expiresAt = safeGetItem(STORAGE_KEYS.expiresAt);
  if (!expiresAt) return true;
  return Date.now() >= Number(expiresAt);
}

/**
 * Checks if the access token will expire within the given buffer (ms).
 * Used for proactive refresh before the token actually expires.
 */
export function isExpiringSoon(bufferMs: number = 60_000): boolean {
  const expiresAt = safeGetItem(STORAGE_KEYS.expiresAt);
  if (!expiresAt) return true;
  return Date.now() >= Number(expiresAt) - bufferMs;
}

/**
 * Gets the current access token, or null if not available.
 */
export function getAccessToken(): string | null {
  return safeGetItem(STORAGE_KEYS.accessToken);
}

/**
 * Gets the current refresh token, or null if not available.
 */
export function getRefreshToken(): string | null {
  return safeGetItem(STORAGE_KEYS.refreshToken);
}

/**
 * Gets the stored expiry time (ms since epoch), or null.
 */
export function getExpiresAt(): number | null {
  const val = safeGetItem(STORAGE_KEYS.expiresAt);
  return val ? Number(val) : null;
}

/**
 * Stores new tokens from a refresh response.
 * The refresh endpoint returns `accessToken` (not `token` like the login endpoint).
 */
export function setTokensFromRefresh(response: {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number | null;
}): void {
  safeSetItem(STORAGE_KEYS.accessToken, response.accessToken);
  if (response.refreshToken) {
    safeSetItem(STORAGE_KEYS.refreshToken, response.refreshToken);
  }

  // Use server-provided expiresAt, or decode from JWT, or fallback to 1h
  let expiresAt: number;
  if (response.expiresAt) {
    expiresAt = response.expiresAt;
  } else {
    const payload = decodeJwtPayload(response.accessToken);
    if (payload && typeof payload.exp === 'number') {
      expiresAt = payload.exp * 1000;
    } else {
      expiresAt = Date.now() + DEFAULT_TTL_MS;
    }
  }
  safeSetItem(STORAGE_KEYS.expiresAt, expiresAt.toString());
}

/**
 * Stores auth tokens received from the social login callback URL fragment.
 * Similar to setTokens but accepts individual values instead of an API response.
 */
export function setTokensFromCallback(tokens: {
  accessToken: string;
  refreshToken: string;
  userId: string;
  slug: string;
}): void {
  try {
    safeSetItem(STORAGE_KEYS.accessToken, tokens.accessToken);
    if (tokens.refreshToken) {
      safeSetItem(STORAGE_KEYS.refreshToken, tokens.refreshToken);
    }
    safeSetItem(STORAGE_KEYS.userId, tokens.userId);
    safeSetItem(STORAGE_KEYS.slug, tokens.slug);

    // Derive expiresAt from JWT exp claim
    const payload = decodeJwtPayload(tokens.accessToken);
    if (payload && typeof payload.exp === 'number') {
      safeSetItem(STORAGE_KEYS.expiresAt, String(payload.exp * 1000));
    } else {
      safeSetItem(STORAGE_KEYS.expiresAt, String(Date.now() + DEFAULT_TTL_MS));
    }
  } catch {
    // Graceful degradation for localStorage failures
  }
}
